import {
  BufferAttribute,
  BufferGeometry,
  Group,
  InterleavedBufferAttribute,
  Intersection,
  Matrix4,
  Ray,
  Raycaster,
  Sphere,
  Vector3,
} from 'three';

const inverseMatrix = new Matrix4();
const localRay = new Ray();
const boundingSphere = new Sphere();

/**
 * A bounds-based raycaster for use with any object that has a buffer geometry but for which we don't want to actually
 *   render a mesh.
 *
 * To use, add a `{ raycastGeometry: BufferGeometry }` property to a visual's `userData` and set
 *   `raycast={raycastBufferGeometry}`. Make sure to disable raycasting on any visible child mesh for performance.
 *
 * Based on `Mesh._computeIntersections` from three.js ; esp. the `checkTriangleIntersection` implementation and the
 *   use of indexed geometry when possible.
 */
export function raycastBufferGeometry(this: Group, raycaster: Raycaster, intersects: Intersection[]): void {
  const geometry = this.userData.raycastGeometry as BufferGeometry | undefined;
  if (geometry == null) return;

  const position = geometry.getAttribute('position');
  if (position == null) return;

  if (geometry.boundingSphere === null) geometry.computeBoundingSphere();
  boundingSphere.copy(geometry.boundingSphere!).applyMatrix4(this.matrixWorld);
  if (raycaster.ray.intersectsSphere(boundingSphere) === null) return;

  inverseMatrix.copy(this.matrixWorld).invert();
  localRay.copy(raycaster.ray).applyMatrix4(inverseMatrix);

  if (geometry.index != null) {
    for (let i = 0; i < geometry.index.count; i += 3) {
      const a = geometry.index.getX(i);
      const b = geometry.index.getX(i + 1);
      const c = geometry.index.getX(i + 2);

      checkTriangleIntersection(this, position, raycaster, a, b, c, intersects);
    }
  } else {
    for (let i = 0; i < position.count; i += 3) {
      checkTriangleIntersection(this, position, raycaster, i, i + 1, i + 2, intersects);
    }
  }
}

const vecA = new Vector3();
const vecB = new Vector3();
const vecC = new Vector3();
const intersectionPoint = new Vector3();

function checkTriangleIntersection(
  object: Group,
  position: BufferAttribute | InterleavedBufferAttribute,
  raycaster: Raycaster,

  a: number,
  b: number,
  c: number,

  intersects: Intersection[],
): void {
  vecA.fromBufferAttribute(position, a);
  vecB.fromBufferAttribute(position, b);
  vecC.fromBufferAttribute(position, c);

  if (localRay.intersectTriangle(vecA, vecB, vecC, false, intersectionPoint) == null) return;

  intersectionPoint.applyMatrix4(object.matrixWorld);
  const distance = raycaster.ray.origin.distanceTo(intersectionPoint);
  if (distance < raycaster.near || distance > raycaster.far) return;

  intersects.push({ object, point: intersectionPoint.clone(), distance });
}
